import { WarningCircleIcon } from '@phosphor-icons/react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useDialogContentContainer,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { DIALOG_PINNED_FOOTER } from '@/lib/ui-tokens';

export type ConfirmVariant = 'info' | 'destructive' | 'validation';

export type ConfirmDetail = {
  icon: React.ComponentType<{ className?: string }>;
  text: string;
};

export interface ConfirmProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant?: ConfirmVariant;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
  confirmDisabled?: boolean;
  children?: React.ReactNode;
  details?: ConfirmDetail[];
  typedConfirmation?: string;
  header?: React.ReactNode;
  /**
   * Pass-through to DialogContent (rev 5 D3), both additive: unset renders
   * exactly as before either prop existed. Every A5 confirm on a paid
   * surface (provider-create, "Replace your picks?", Meta's audience-mode
   * propose, discard) passes `overlayStrength="strong"` and
   * `surface="elevated"` — see `dialog.tsx`'s `DialogContentProps` for what
   * each one changes.
   */
  overlayStrength?: 'default' | 'strong';
  surface?: 'default' | 'elevated';
}

export function Confirm({
  open,
  onOpenChange,
  variant = 'info',
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  loading: externalLoading,
  confirmDisabled = false,
  children,
  details,
  typedConfirmation,
  header,
  overlayStrength,
  surface,
}: ConfirmProps): JSX.Element {
  const parentDialogContainer = useDialogContentContainer();
  const [internalLoading, setInternalLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [typedValue, setTypedValue] = React.useState('');
  const loading = externalLoading ?? internalLoading;

  React.useEffect(() => {
    if (!open) {
      setTypedValue('');
      setError(null);
    }
  }, [open]);

  const typedMatches =
    !typedConfirmation || typedValue.trim().toLowerCase() === typedConfirmation.toLowerCase();
  const canConfirm = !loading && !confirmDisabled && typedMatches;

  const handleConfirm = async (): Promise<void> => {
    setError(null);
    try {
      const result = onConfirm();
      if (result instanceof Promise) {
        setInternalLoading(true);
        await result;
        setInternalLoading(false);
        onOpenChange(false);
      } else {
        onOpenChange(false);
      }
    } catch (err) {
      setInternalLoading(false);
      setError(
        err instanceof Error
          ? err.message
          : "Couldn't complete. Check your connection and try again.",
      );
    }
  };

  const handleCancel = (): void => {
    onCancel?.();
    onOpenChange(false);
  };

  const handleOpenChange = (next: boolean): void => {
    if (loading) return;
    if (!next) setError(null);
    onOpenChange(next);
  };

  const hasHeader = Boolean(header);
  const dialogSize = hasHeader || details || typedConfirmation ? 'default' : 'sm';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        size={dialogSize}
        hideCloseButton={loading}
        portalContainer={parentDialogContainer}
        overlayStrength={overlayStrength}
        surface={surface}
      >
        {hasHeader && <div className="flex items-center justify-center gap-3 pb-4">{header}</div>}

        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription className="mt-2">{description}</DialogDescription>}
        </DialogHeader>

        {details && details.length > 0 && (
          <div className="space-y-2 rounded-lg border border-border/40 bg-muted/20 p-3">
            {details.map((d, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                <d.icon className="size-4 shrink-0 mt-0.5 text-muted-foreground/70" />
                <span>{d.text}</span>
              </div>
            ))}
          </div>
        )}

        {children}

        {typedConfirmation && (
          <div className="space-y-1.5">
            <Label htmlFor="typed-confirm" className="text-detail text-muted-foreground">
              Type{' '}
              <span className="font-mono font-medium text-foreground">{typedConfirmation}</span> to
              confirm
            </Label>
            <Input
              id="typed-confirm"
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              placeholder={typedConfirmation}
              autoComplete="off"
              disabled={loading}
              className="font-mono"
            />
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            <WarningCircleIcon className="size-4 mt-0.5 shrink-0" weight="regular" />
            <span className="flex-1">{error}</span>
          </div>
        )}

        <DialogFooter className={DIALOG_PINNED_FOOTER}>
          <Button variant="outline" onClick={handleCancel} disabled={loading}>
            {cancelLabel ?? 'Cancel'}
          </Button>
          <Button
            variant={variant === 'destructive' ? 'destructive' : 'neutral'}
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {loading ? (
              <>
                <Spinner className="size-4 mr-2" />
                <span>Working…</span>
              </>
            ) : (
              <span>{error ? 'Try again' : (confirmLabel ?? 'Confirm')}</span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
